// src/Pages/EditRFQ.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../Components/Sidebar';
import '../Style/CreateRFQ.css'; // reuse styling
import {
  FaCloudUploadAlt,
  FaSpinner,
  FaTimes
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { db, storage } from '../firebaseConfig';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/* ───────── helpers ───────── */
const debounce = (fn, wait = 300) => {
  let t;
  return (...a) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), wait);
  };
};

const fmtDate = d => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  if (d.toDate) return d.toDate().toISOString().slice(0, 10);
  if (d instanceof Date) return d.toISOString().slice(0, 10);
  return '';
};

const fileKind = (name, type = '') => {
  if (/pdf$/i.test(name) || type === 'application/pdf') return 'pdf';
  if (/csv$/i.test(name) || type === 'text/csv') return 'csv';
  if (/image\//.test(type) || /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(name)) return 'image';
  return 'other';
};

const MAX_FILES = 10;
const MAX_SIZE_MB = 10;

/* ───────── component ───────── */
export default function EditRFQ() {
  const { id } = useParams();
  const navigate = useNavigate();

  /* base fields */
  const [rfqNo, setRfqNo] = useState('Loading…');
  const [status, setStatus] = useState('Order pending');
  const [customer, setCustomer] = useState('');
  const [customers, setCustomers] = useState([]);

  const [rfqDate, setRfqDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [deliveryTerms, setDeliveryTerms] = useState('');
  const [customerRef, setCustomerRef] = useState('');
  const [notes, setNotes] = useState('');

  /* files */
  const [files, setFiles] = useState([]);        // new File[]
  const [fileMeta, setFileMeta] = useState([]);  // existing + new meta
  const [dragActive, setDrag] = useState(false);

  /* ui flags */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* validation */
  const [errors, setErrors] = useState({
    rfqNo: false,
    customer: false,
    status: false
  });

  const fileInputRef = useRef(null);

  /* ───────── load RFQ + customers ───────── */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // RFQ
        const snap = await getDoc(doc(db, 'RFQs', id));
        if (!snap.exists()) {
          toast.error('RFQ not found.', { className: 'toast-error' });
          navigate('/Jobs');
          return;
        }
        const data = snap.data();

        setRfqNo(data.rfqNo || data.rfqId || '—');
        setStatus(data.status || 'Order pending');
        setCustomer(data.customer || '');
        setRfqDate(fmtDate(data.rfqDate || data.createdAt));
        setExpiryDate(fmtDate(data.expiryDate));
        setPaymentTerms(data.paymentTerms || '');
        setDeliveryTerms(data.deliveryTerms || '');
        setCustomerRef(data.customerRef || '');
        setNotes(data.notes || '');

        // Existing file meta
        const existingMeta = (data.fileMeta && data.fileMeta.length
          ? data.fileMeta
          : (data.fileUrls || []).map(u => ({
              name: u.split('?')[0].split('/').pop(),
              url: u,
              kind: fileKind(u),
              preview: /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(u) ? u : null
            }))
        ).map(m => ({ ...m, existing: true }));

        setFileMeta(existingMeta);

        // Customers
        const custSnap = await getDocs(collection(db, 'Customers'));
        setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
        toast.error('Failed to load RFQ.', { className: 'toast-error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  /* ───────── debounced setters ───────── */
  const debR = useCallback(debounce(v => setRfqNo(v), 300), []);
  const debNotes = useCallback(debounce(v => setNotes(v), 300), []);
  const debPay = useCallback(debounce(v => setPaymentTerms(v), 300), []);
  const debDel = useCallback(debounce(v => setDeliveryTerms(v), 300), []);
  const debCustRef = useCallback(debounce(v => setCustomerRef(v), 300), []);

  /* ───────── validation ───────── */
  const validate = useCallback(() => {
    const newErrors = {
      rfqNo: !rfqNo.trim(),
      customer: !customer,
      status: !status
    };
    setErrors(newErrors);
    return !newErrors.rfqNo && !newErrors.customer && !newErrors.status;
  }, [rfqNo, customer, status]);

  /* ───────── file helpers ───────── */
  const addFiles = useCallback(newFiles => {
    if (!newFiles.length) return;
    let existing = [...files];
    let meta = [...fileMeta];

    for (const f of newFiles) {
      if (existing.length + meta.filter(m => m.existing).length >= MAX_FILES) {
        toast.error(`Max ${MAX_FILES} files reached.`, { className: 'toast-error' });
        break;
      }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(`${f.name} exceeds ${MAX_SIZE_MB} MB.`, { className: 'toast-error' });
        continue;
      }
      const kind = fileKind(f.name, f.type);
      existing.push(f);
      meta.push({
        name: f.name,
        kind,
        preview: kind === 'image' ? URL.createObjectURL(f) : null,
        existing: false
      });
    }
    setFiles(existing);
    setFileMeta(meta);
  }, [files, fileMeta]);

  const handleInputFiles = e => addFiles(Array.from(e.target.files || []));
  const onDragOver  = e => { e.preventDefault(); setDrag(true); };
  const onDragLeave = () => setDrag(false);
  const onDrop      = e => { e.preventDefault(); setDrag(false); addFiles(Array.from(e.dataTransfer.files || [])); };
  const openPicker  = () => fileInputRef.current?.click();

  const removeFile = idx => {
    const tgt = fileMeta[idx];
    if (!tgt) return;

    // Remove from new‑files array if it was new
    if (!tgt.existing) {
      const newIdx = fileMeta
        .filter(m => !m.existing)
        .findIndex((m, i) => fileMeta.findIndex(f => f === m) === idx);
      setFiles(f => f.filter((_, i) => i !== newIdx));
      tgt.preview && URL.revokeObjectURL(tgt.preview);
    }
    setFileMeta(m => m.filter((_, i) => i !== idx));
  };

  useEffect(() => () => {
    // cleanup previews on unmount
    fileMeta.forEach(m => !m.existing && m.preview && URL.revokeObjectURL(m.preview));
  }, [fileMeta]);

  /* ───────── upload helper ───────── */
  const uploadAll = async () => {
    // nothing new
    if (!files.length) {
      return {
        fileMeta: fileMeta.map(({ existing, preview, ...rest }) => rest),
        fileUrls: fileMeta.filter(m => m.url).map(m => m.url)
      };
    }
    // upload new ones
    const uploaded = await Promise.all(
      files.map(async f => {
        const storageRef = ref(storage, `RFQs/${rfqNo}/${Date.now()}_${f.name}`);
        await uploadBytes(storageRef, f);
        const url = await getDownloadURL(storageRef);
        return { name: f.name, url, kind: fileKind(f.name, f.type) };
      })
    );

    const existMeta = fileMeta.filter(m => m.existing).map(({ existing, preview, ...rest }) => rest);
    const meta = [...existMeta, ...uploaded];

    const existUrls = fileMeta.filter(m => m.url).map(m => m.url);
    const urls = [...existUrls, ...uploaded.map(u => u.url)];

    return { fileMeta: meta, fileUrls: urls };
  };

  /* ───────── reset helper ───────── */
  const resetForm = async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'RFQs', id));
      if (!snap.exists()) return;
      const d = snap.data();

      setRfqNo(d.rfqNo || d.rfqId || '—');
      setStatus(d.status || 'Order pending');
      setCustomer(d.customer || '');
      setRfqDate(fmtDate(d.rfqDate || d.createdAt));
      setExpiryDate(fmtDate(d.expiryDate));
      setPaymentTerms(d.paymentTerms || '');
      setDeliveryTerms(d.deliveryTerms || '');
      setCustomerRef(d.customerRef || '');
      setNotes(d.notes || '');

      const meta = (d.fileMeta?.length ? d.fileMeta : (d.fileUrls || []).map(u => ({
        name: u.split('?')[0].split('/').pop(),
        url: u,
        kind: fileKind(u),
        preview: /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(u) ? u : null
      }))).map(m => ({ ...m, existing: true }));

      setFiles([]);
      setFileMeta(meta);
      setErrors({ rfqNo: false, customer: false, status: false });
    } finally {
      setLoading(false);
    }
  };

  /* ───────── save ───────── */
  const saveRFQ = async e => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please correct highlighted fields.', { className: 'toast-error' });
      return;
    }
    setSaving(true);
    try {
      /* —— duplicate RFQ‑No guard —— */
      const dupQ = query(collection(db, 'RFQs'), where('rfqNo', '==', rfqNo.trim()));
      const dupSnap = await getDocs(dupQ);
      if (
        !dupSnap.empty &&
        dupSnap.docs.some(d => d.id !== id)      // ignore the current doc
      ) {
        toast.error(`RFQ number "${rfqNo}" already exists. Pick a unique number.`, {
          className: 'toast-error'
        });
        setErrors(prev => ({ ...prev, rfqNo: true }));
        setSaving(false);
        return;
      }

      const { fileMeta: newMeta, fileUrls } = await uploadAll();

      await updateDoc(doc(db, 'RFQs', id), {
        rfqNo: rfqNo.trim(),
        customer,
        status,
        rfqDate: rfqDate || '',
        expiryDate: expiryDate || '',
        paymentTerms: paymentTerms.trim() || '—',
        deliveryTerms: deliveryTerms.trim() || '—',
        customerRef: customerRef.trim() || '—',
        notes: notes.trim() || '—',
        fileMeta: newMeta,
        fileUrls,
        updatedAt: new Date().toISOString()
      });

      toast.success('RFQ updated successfully!', { className: 'toast-success' });
      navigate('/Jobs'); // or your RFQ list route
    } catch (err) {
      console.error(err);
      toast.error('Could not update RFQ.', { className: 'toast-error' });
    } finally {
      setSaving(false);
    }
  };

  /* ───────── render ───────── */
  if (loading) {
    return (
      <div className="createrfq-page">
        <Sidebar />
        <div className="saving-overlay">
          <FaSpinner className="spin" size={56} />
          <p>Loading RFQ…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="createrfq-page">
      <ToastContainer position="top-right" autoClose={2500} />
      <Sidebar />

      <main className="createrfq-main">
        <div className="title-row">
          <h2>Edit RFQ</h2>
          <span className="badge-id">{rfqNo}</span>
        </div>

        <form onSubmit={saveRFQ}>
          {/* ───────── details ───────── */}
          <section className="card">
            <h3>RFQ Details</h3>
            <div className="grid-4">
              {/* RFQ No */}
              <div className="field">
                <label>RFQ No <span className="required">*</span></label>
                <input
                  defaultValue={rfqNo}
                  onChange={e => debR(e.target.value)}
                  className={errors.rfqNo ? 'input-error' : ''}
                />
                {errors.rfqNo && <span className="error-text">Required &amp; unique</span>}
              </div>

              {/* Customer */}
              <div className="field">
                <label>Customer <span className="required">*</span></label>
                <select
                  value={customer}
                  onChange={e => setCustomer(e.target.value)}
                  className={errors.customer ? 'input-error' : ''}
                >
                  <option value="">Select customer</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
                {errors.customer && <span className="error-text">Required</span>}
              </div>

              {/* Status */}
              <div className="field">
                <label>Status <span className="required">*</span></label>
                <select
                  value={status}
                  onChange={e => setStatus(e.target.value)}
                  className={errors.status ? 'input-error' : ''}
                >
                  <option>Order pending</option>
                  <option>Order received</option>
                  <option>Cancelled</option>
                </select>
                {errors.status && <span className="error-text">Required</span>}
              </div>

              {/* Dates */}
              <div className="field">
                <label>RFQ Date</label>
                <input
                  type="date"
                  value={rfqDate}
                  onChange={e => setRfqDate(e.target.value)}
                />
              </div>

              <div className="field">
                <label>Expiry Date</label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={e => setExpiryDate(e.target.value)}
                />
              </div>

              {/* Payment / Delivery */}
              <div className="field">
                <label>Payment Terms</label>
                <input
                  defaultValue={paymentTerms}
                  placeholder="e.g. 30 days"
                  onChange={e => debPay(e.target.value)}
                />
              </div>

              <div className="field">
                <label>Delivery Terms</label>
                <input
                  defaultValue={deliveryTerms}
                  placeholder="e.g. FOB"
                  onChange={e => debDel(e.target.value)}
                />
              </div>

              {/* Customer Ref */}
              <div className="field">
                <label>Customer Reference</label>
                <input
                  defaultValue={customerRef}
                  placeholder="Customer ref / PO"
                  onChange={e => debCustRef(e.target.value)}
                />
              </div>
            </div>

            {/* Notes */}
            <div className="field mt">
              <label>Notes</label>
              <textarea
                rows={3}
                defaultValue={notes}
                placeholder="Additional notes…"
                onChange={e => debNotes(e.target.value)}
              />
            </div>
          </section>

          {/* ───────── uploads ───────── */}
          <section className="card">
            <h3>Attachments</h3>
            <p className="hint">
              Upload images / PDF / CSV (max {MAX_FILES} files, {MAX_SIZE_MB} MB each).
            </p>
            <div
              className={`upload-area ${dragActive ? 'drag' : ''}`}
              onClick={openPicker}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              role="button"
              tabIndex={0}
            >
              <FaCloudUploadAlt size={34} />
              <p>{dragActive ? 'Drop to upload' : 'Click or drag files here'}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf,text/csv,.csv"
                multiple
                hidden
                onChange={handleInputFiles}
              />
            </div>

            {fileMeta.length > 0 && (
              <div className="files-grid">
                {fileMeta.map((f, i) => (
                  <div key={i} className="file-chip">
                    <div className={`preview ${f.kind}`}>
                      {f.kind === 'image' ? (
                        <img src={f.preview || f.url} alt={f.name} />
                      ) : (
                        <span className="badge-kind">{f.kind.toUpperCase()}</span>
                      )}
                    </div>
                    <div className="fname" title={f.name}>{f.name}</div>
                    <button
                      type="button"
                      className="remove-chip"
                      onClick={() => removeFile(i)}
                      aria-label={`Remove ${f.name}`}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ───────── actions ───────── */}
          <section className="card action-card">
            <div className="actions-row">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save RFQ'}
              </button>
              <button
                type="button"
                className="btn-outline"
                onClick={resetForm}
                disabled={saving}
              >
                Reset Form
              </button>
            </div>
          </section>
        </form>
      </main>

      {saving && (
        <div className="saving-overlay">
          <FaSpinner className="spin" size={58} />
          <p>Saving…</p>
        </div>
      )}
    </div>
  );
}
